import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Spinner,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createRole,
  getAllPermissionList,
  getRoleById,
  updateRole,
} from "../../slices/rolesAndPermission/thunk";
import { useFormik } from "formik";
import * as Yup from "yup";
import { hideErrorMessage } from "../../slices/rolesAndPermission/reducer";

const AddPermission = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const roleById = useSelector((state) => state.Roles.roleById);
  const loading = useSelector((state) => state.Roles.loading);
  const permissionList = useSelector((state) => state.Roles.permissionList);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
      navigate("/user-permissions");
    }, 3000);
  };


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: id ? roleById.name : "",
      permission_ids: id ? roleById?.permissions?.map((perm) => perm.id) : [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("User Role is required"),
      permission_ids: Yup.array().min(1, "At least one permission is required"),
    }),
    onSubmit: async (values) => {
      try {
        let response;
        if (id) {
          response = await dispatch(updateRole(id, values));

          const { success, message } = response;
          showToaster(success, message);

        } else {
          response = await dispatch(createRole(values));
          const { success, message } = response;
          showToaster(success, message);
          console.log("response", response, toasterType);
          
        }
      } catch (error) {
        showToaster(false, error?.message || "An error occurred while processing your request");
      }

    },
  });

  const groupPermissionsByModule = (permissions) => {
    const grouped = {};

    permissions.forEach((perm) => {
      const parts = perm.name.split('_');
      const module = parts.slice(0, -1).join('_');
      if (!grouped[module]) {
        grouped[module] = [];
      }
      grouped[module].push(perm);
    });

    const typeOrder = { read: 0, create: 1, update: 2, delete: 3 };
    for (const module in grouped) {
      grouped[module].sort((a, b) => typeOrder[a.type] - typeOrder[b.type]);
    }

    return grouped;
  };

  const groupedPermissions = groupPermissionsByModule(permissionList);

  useEffect(() => {
    try {
      dispatch(getAllPermissionList());
      if (id) {
        dispatch(getRoleById(id));
      }
    } catch (error){}
  }, [dispatch, id]);

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex flex-column gap-4">
          <TitleBar title={`${id ? "Update" : "Create"} User Roles`} showIcon={true} />

          <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
            <Toast isOpen={showToast}>
              <ToastHeader
                icon={toasterType === "danger" ? "danger" : "success"}
                toggle={() => setShowToast(false)}
              >
                {toasterType === "danger" ? "Error" : "Success"}
              </ToastHeader>
              <ToastBody>{toastMessage}</ToastBody>
            </Toast>
          </div>

          <Form onSubmit={formik.handleSubmit}>
            <div className="createUserRoleSection">
              <div className="permissionFormWrap">
                <div className="pt-2 pb-1">
                  <FormGroup>
                    <Label for="name" className="passwordLabel">
                      Enter User Role
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      className="profileInput"
                      id="name"
                      placeholder="Enter Role"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.name && !!formik.errors.name}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <FormFeedback>{formik.errors.name}</FormFeedback>
                    ) : null}
                  </FormGroup>
                </div>


                <FormGroup>
                  <Label for="permission_ids" className="passwordLabel">
                    Permissions
                  </Label>
                  {Object.entries(groupedPermissions).map(([module, perms]) => (
                    <div key={module} className="permissionContainer mt-2">
                      <div className="permissionItem selectAll">
                        <Input
                          type="checkbox"
                          id={`selectAll-${module}`}
                          checked={perms.every((p) => formik.values.permission_ids.includes(p.id))}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            const moduleIds = perms.map((p) => p.id);
                            const updatedPermissions = isChecked
                              ? Array.from(new Set([...formik.values.permission_ids, ...moduleIds]))
                              : formik.values.permission_ids.filter((id) => !moduleIds.includes(id));
                            formik.setFieldValue("permission_ids", updatedPermissions);
                          }}
                        />
                        <Label htmlFor={`selectAll-${module}`} className="mb-0 text-capitalize">
                          Select All {module.replace(/_/g, " ")} Permissions
                        </Label>
                      </div>

                      <div className="permissionsWrap">
                        {perms.map((perm) => (
                          <div key={perm.id} className="permissionItem">
                            <Input
                              type="checkbox"
                              id={`perm-${perm.id}`}
                              checked={formik.values.permission_ids.includes(perm.id)}
                              onChange={() => {
                                const updated = formik.values.permission_ids.includes(perm.id)
                                  ? formik.values.permission_ids.filter((id) => id !== perm.id)
                                  : [...formik.values.permission_ids, perm.id];
                                formik.setFieldValue("permission_ids", updated);
                              }}
                              invalid={formik.touched.permission_ids && !!formik.errors.permission_ids}
                            />
                            <Label htmlFor={`perm-${perm.id}`} className="mb-0 text-capitalize">
                              {perm.type}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {formik.touched.permission_ids && formik.errors.permission_ids ? (
                    <div className="text-danger">{formik.errors.permission_ids}</div>
                  ) : null}
                </FormGroup>
              </div>

              <div className="permissionBtnWrap">
                <Button className="addRoleCancelBtn" color="warning" onClick={() => navigate(-1)}>
                  Close without saving
                </Button>
                <Button type="submit" color="primary" className="addRoleBtn">
                  {
                    id && loading.update ? <Spinner /> :
                      !id && loading.create ? <Spinner /> :
                        id ? "Update" : "Add"
                  }
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default AddPermission;
